import { HackathonPayload } from '../services/hackathon.service';
import { IdeaPayload } from '../services/idea.service';
import { SessionPayload } from '../services/session.service';
import { SessionLiveState } from '../services/sessionLive.service';

export interface SessionRoles {
    judging: boolean;
    hosting: boolean;
    presenting: boolean;
}

export const sessionRoles = (session: SessionPayload, hackathon: HackathonPayload, email: string | undefined): SessionRoles => {
    if (!email) {
        return { judging: false, hosting: false, presenting: false };
    }
    const sessionIdeas = new Set(session.ideas);
    return {
        judging: session.judges.some(j => j.email === email),
        hosting: session.hosts.some(h => h.email === email),
        presenting: hackathon.ideas.some(i => sessionIdeas.has(i._id) && i.members.some(m => m.email === email))
    };
}

/**
 * Whether participants have anything to score: participant scoring is on and at least one category (in the room, when
 * given) isn't judges-only. When every category is judges-only, participants are left out of voting entirely, prompts included.
 */
export const participantsScore = (hackathon: HackathonPayload, session?: Pick<SessionPayload, 'categories'>) => {
    if (!hackathon.participantScores) {
        return false;
    }
    const inRoom = session ? new Set(session.categories) : null;
    return hackathon.categoryMeta.some(meta => !meta.judgeOnly && (!inRoom || (!!meta._id && inRoom.has(meta._id))));
}

/**
 * The teams a room expects scores for: once the room has presented anyone, the teams that have presented or are on
 * stage (a team that never presents doesn't hold up "all done"); before that, or with no presentations at all, the whole lineup.
 */
export const dueTeams = (live: Pick<SessionLiveState, 'ideas' | 'presented' | 'activeIdea'>) => {
    const started = live.presented.length > 0 || !!live.activeIdea;
    return started ? live.ideas.filter(id => live.presented.includes(id) || live.activeIdea === id) : live.ideas;
}

export const hasAnyRole = (roles: SessionRoles) => roles.judging || roles.hosting || roles.presenting;

export const roleLabels = (roles: SessionRoles) => [
    roles.presenting && 'Presenting',
    roles.judging && 'Judging',
    roles.hosting && 'Hosting'
].filter(Boolean) as string[];

export const categoryLabels = (hackathon: HackathonPayload, ids: string[]) => {
    const byId = new Map(hackathon.categoryMeta.map((m, i) => [m._id, hackathon.categories[i]]));
    return ids.map(id => byId.get(id)).filter(Boolean) as string[];
}

export interface SessionWarning {
    severity: 'warning' | 'info';
    message: string;
}

/**
 * Setup problems organizers should know about. None of these block anything.
 */
export const sessionWarnings = (hackathon: HackathonPayload, sessions: SessionPayload[]): SessionWarning[] => {
    if (sessions.length === 0) {
        return [];
    }
    const warnings: SessionWarning[] = [];
    const assigned = new Set(sessions.flatMap(s => s.ideas));
    const unassigned = hackathon.ideas.filter(i => !i.archived && !i.disqualified && !assigned.has(i._id));
    if (unassigned.length) {
        warnings.push({
            severity: 'warning',
            message: `${unassigned.length} team${unassigned.length === 1 ? ' isn\'t' : 's aren\'t'} in any session: ${unassigned.map(i => i.title).join(', ')}`
        });
    }

    const covered = new Set(sessions.flatMap(s => s.categories));
    const uncovered = hackathon.categoryMeta
        .map((m, i) => ({ id: m._id, label: hackathon.categories[i] }))
        .filter(c => c.id && !covered.has(c.id));
    if (uncovered.length) {
        warnings.push({ severity: 'warning', message: `No session judges ${uncovered.map(c => c.label).join(', ')}` });
    }

    sessions.forEach(session => {
        if (session.judges.length === 0) {
            warnings.push({ severity: 'warning', message: `${session.name} has no judges` });
        }
        if (session.categories.length === 0) {
            warnings.push({ severity: 'warning', message: `${session.name} has no categories to judge` });
        }
        if (session.ideas.length === 0) {
            warnings.push({ severity: 'info', message: `${session.name} has no teams yet` });
        }
        const sessionIdeas = hackathon.ideas.filter(i => session.ideas.includes(i._id));
        session.judges.forEach(judge => {
            const own = sessionIdeas.find(i => i.members.some(m => m.email === judge.email));
            if (own) {
                warnings.push({ severity: 'warning', message: `${judge.username} judges ${session.name} but is on "${own.title}", which presents there` });
            }
        });
    });
    return warnings;
}

export interface VoteEligibility {
    allowed: boolean;
    asJudge: boolean;
    // why not, phrased for the voter
    reason: string;
    // the reason is about the room's voting, so the room page is worth a link
    inRoom?: boolean;
}

/**
 * Whether an idea is an entry: anything not deleted, even with nobody on it. Mirrors the server, which
 * leaves only deleted ideas out of rooms and judging.
 */
export const isEntry = (idea: Pick<IdeaPayload, 'archived'>) => !idea.archived;

/** Why an idea can't be scored at all, in plain words, or null (mirrors the server). */
export const notEntryReason = (hackathon: HackathonPayload, ideaId: string) => {
    const idea = hackathon.ideas.find(i => i._id === ideaId);
    if (!idea || idea.archived) {
        return 'This idea was deleted, so there\'s nothing to score';
    }
    return null;
}

/**
 * Mirrors the server's voting rules so the UI only offers votes that will be accepted:
 * - hackathon-wide voting is on, and (with several rooms) the room's own switch is on
 * - judges score only in the sessions they judge
 * - everyone else (when participant voting is on) can vote on any team in any session
 */
export const voteEligibility = (
    hackathon: HackathonPayload,
    session: SessionPayload,
    live: SessionLiveState | undefined,
    ideaId: string,
    email: string | undefined,
    // single: the hackathon has one room, whose own switch is then ignored
    options: { single: boolean, votingPeriodOn: boolean }
): VoteEligibility => {
    const no = (reason: string, asJudge = false, inRoom = false) => ({ allowed: false, asJudge, reason, inRoom });
    if (!email) {
        return no('Sign in to vote');
    }
    const asJudge = session.judges.some(j => j.email === email);
    // the live lineup when there is one: it reflects teams the hosts just left out or put back
    if (!(live?.ideas ?? session.ideas).includes(ideaId)) {
        return no(notEntryReason(hackathon, ideaId)
            || (live?.leftOut.includes(ideaId) ? `The hosts left this team out of ${session.name}, so it isn't being judged there` : `This team isn't presenting in ${session.name}`), asJudge);
    }
    if (!asJudge && hackathon.judges.some(j => j.email === email)) {
        return no(`You're not judging ${session.name}`);
    }
    if (!asJudge && !participantsScore(hackathon, session)) {
        return no(`Only ${session.name}'s judges vote here`);
    }
    if (!asJudge && hackathon.ideas.find(i => i._id === ideaId)?.members.some(m => m.email === email)) {
        return no('You can\'t vote for your own team');
    }
    if (!options.votingPeriodOn) {
        // once anyone has voted, voting being off means it has closed rather than not started
        const hasVotes = hackathon.votesCast ?? (hackathon.results || []).some(r => r.ideas.some(i => (i.judgeVotes || []).some(v => v.score > 0) || i.participantVoteAverage > 0));
        return no(hasVotes ? 'Voting is closed' : 'Voting hasn\'t opened yet. When it opens, you\'ll score each category here from 1 (weak) to 10 (excellent).', asJudge, !hasVotes);
    }
    if (!options.single && !live?.votingOpen) {
        return no(`Voting isn't open in ${session.name} right now`, asJudge, true);
    }
    return { allowed: true, asJudge, reason: '' };
}
